import React from 'react';

import { bffQuery, bffMutation } from '../lib/bffClient';
import { useOptionalClinicContext } from '../context/ClinicContext.jsx';

/**
 * The active clinic's «پیش‌پرداخت نوبت» policy (PM-0657, contract §5.2 / §6.1).
 *
 * Read by پذیرش (whether «ثبت نوبت» shows the deposit block, what it suggests,
 * whether a row is missing a REQUIRED deposit) and by «کلینیک من ▸ پیش‌پرداخت».
 *
 * Asked in its OWN document — a panel newer than its BFF loses only this answer
 * (null = behave as before: no deposit block, no chip), never the reception day.
 * One answer per clinic, kept five minutes and shared by every screen; a failed
 * read is forgotten so the next mount asks again; a save replaces the answer.
 */

export const PREPAYMENT_POLICY_FIELDS = `
  mode amountMode fixedAmountRial percent refundWindowHours
  noShowOutcome noShowFeePercent isDefault currency
`;

export const PREPAYMENT_POLICY_QUERY = `
  query PrepaymentPolicy {
    prepaymentPolicy { ${PREPAYMENT_POLICY_FIELDS} }
  }
`;

export const UPDATE_PREPAYMENT_POLICY_MUTATION = `
  mutation UpdatePrepaymentPolicy($input: PrepaymentPolicyInput!) {
    updatePrepaymentPolicy(input: $input) { ${PREPAYMENT_POLICY_FIELDS} }
  }
`;

const TTL_MS = 5 * 60 * 1000;

// clinicId → { promise, value, at } — `value` once answered.
const answers = new Map();
const listeners = new Set();

function load(clinicId, { force = false } = {}) {
  const hit = answers.get(clinicId);
  if (!force && hit && (!('value' in hit) || Date.now() - hit.at < TTL_MS)) return hit.promise;
  const entry = {};
  entry.promise = bffQuery(PREPAYMENT_POLICY_QUERY, {}, 'PrepaymentPolicy')
    .then((data) => {
      const value = data?.prepaymentPolicy && typeof data.prepaymentPolicy === 'object'
        ? data.prepaymentPolicy
        : null;
      if (!value) throw new Error('prepaymentPolicy missing');
      entry.value = value;
      entry.at = Date.now();
      return value;
    })
    .catch((err) => {
      answers.delete(clinicId);
      throw err;
    });
  answers.set(clinicId, entry);
  return entry.promise;
}

function known(clinicId) {
  const hit = clinicId ? answers.get(clinicId) : null;
  return hit && 'value' in hit ? hit.value : null;
}

function remember(clinicId, value) {
  answers.set(clinicId, { promise: Promise.resolve(value), value, at: Date.now() });
  listeners.forEach((fn) => fn(clinicId, value));
}

/** Tests only: forget every answer. */
export function resetPrepaymentPolicy() {
  answers.clear();
}

/**
 * `{ policy, loading, error, reload, save }`. `policy` is null while unknown and
 * after a failed read; callers treat null as OFF.
 */
export function usePrepaymentPolicy() {
  const clinic = useOptionalClinicContext();
  const clinicId = clinic?.activeMembership?.clinicId || '';
  const [state, setState] = React.useState(() => ({
    clinicId, policy: known(clinicId), loading: !known(clinicId), error: null,
  }));

  const fetchPolicy = React.useCallback((force = false) => {
    if (!clinicId) {
      setState({ clinicId, policy: null, loading: false, error: null });
      return undefined;
    }
    let live = true;
    setState((prev) => ({ ...prev, clinicId, loading: !known(clinicId) || force, error: null }));
    load(clinicId, { force }).then(
      (policy) => { if (live) setState({ clinicId, policy, loading: false, error: null }); },
      (error) => { if (live) setState({ clinicId, policy: null, loading: false, error }); },
    );
    return () => { live = false; };
  }, [clinicId]);

  React.useEffect(() => fetchPolicy(false), [fetchPolicy]);

  React.useEffect(() => {
    const fn = (id, policy) => {
      if (id === clinicId) setState({ clinicId, policy, loading: false, error: null });
    };
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, [clinicId]);

  const reload = React.useCallback(() => { fetchPolicy(true); }, [fetchPolicy]);

  const save = React.useCallback(async (input) => {
    const data = await bffMutation(UPDATE_PREPAYMENT_POLICY_MUTATION, { input }, 'UpdatePrepaymentPolicy');
    const policy = data?.updatePrepaymentPolicy || null;
    if (policy && clinicId) remember(clinicId, policy);
    return policy;
  }, [clinicId]);

  const current = state.clinicId === clinicId ? state : { policy: known(clinicId), loading: !known(clinicId), error: null };
  return { policy: current.policy, loading: current.loading, error: current.error, reload, save };
}

/** Just the mode — 'OFF' while unknown, so no screen shows a deposit control on a guess. */
export function usePrepaymentMode() {
  const { policy } = usePrepaymentPolicy();
  return policy?.mode || 'OFF';
}

export default usePrepaymentPolicy;
